import { useQuery } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../../lib/graphql';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Text,
  Grid,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DashboardChart,
  MetricCard,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Badge,
  LoadingSpinner
} from '@tonic/shared/components/ui';
import {
  Users,
  ShoppingBag,
  Repeat,
  TrendingUp,
  Wrench,
  RefreshCw,
  Droplet,
  Recycle,
  Calendar,
  Star
} from 'lucide-react';

// Mock data for development
const MOCK_DATA = {
  customerDashboard: {
    totalCustomers: 15000,
    newCustomersToday: 125,
    activeCustomersThisWeek: 8500,
    averageLifetimeValue: 2500,
    loyaltyTierBreakdown: [
      { tier: 'Bronze', count: 5000, percentage: 33.3 },
      { tier: 'Silver', count: 4000, percentage: 26.7 },
      { tier: 'Gold', count: 3500, percentage: 23.3 },
      { tier: 'Platinum', count: 2500, percentage: 16.7 }
    ],
    reCommerceActivity: {
      totalRepairs: 1250,
      totalResales: 850,
      totalRentals: 620,
      totalRefills: 1500,
      totalRecycled: 750,
      averageReCommerceScore: 85,
      monthlyTrends: [
        { month: '2024-01', repairs: 400, resales: 250, rentals: 180, refills: 450, recycled: 220 },
        { month: '2024-02', repairs: 420, resales: 280, rentals: 200, refills: 480, recycled: 240 },
        { month: '2024-03', repairs: 430, resales: 320, rentals: 240, refills: 570, recycled: 290 }
      ],
      categoryBreakdown: [
        { category: 'Furniture', repairs: 500, resales: 300, rentals: 250, refills: 0, recycled: 200 },
        { category: 'Electronics', repairs: 400, resales: 250, rentals: 150, refills: 0, recycled: 300 },
        { category: 'Home Decor', repairs: 350, resales: 300, rentals: 220, refills: 0, recycled: 250 }
      ],
      sustainabilityImpact: [
        { metric: 'Carbon Saved', value: '250kg', change: 15 },
        { metric: 'Materials Recycled', value: '1.2t', change: 8 },
        { metric: 'Water Saved', value: '5000L', change: 12 }
      ]
    },
    customerEngagement: {
      monthlyActive: 12000,
      retentionRate: 85,
      npsScore: 72,
      satisfactionScore: 92,
      monthlyTrends: [
        { month: '2024-01', activeUsers: 11000, retention: 82, satisfaction: 90 },
        { month: '2024-02', activeUsers: 11500, retention: 84, satisfaction: 91 },
        { month: '2024-03', activeUsers: 12000, retention: 85, satisfaction: 92 }
      ]
    }
  }
};

// Update the GraphQL query to match server schema
const GET_CUSTOMER_ANALYTICS = `
  query GetCustomerAnalytics {
    customerDashboard {
      totalCustomers
      newCustomersToday
      activeCustomersThisWeek
      averageLifetimeValue
      loyaltyTierBreakdown {
        tier
        count
        percentage
      }
      reCommerceActivity {
        totalRepairs
        totalResales
        totalRentals
        totalRefills
        totalRecycled
        averageReCommerceScore
        monthlyTrends {
          month
          repairs
          resales
          rentals
          refills
          recycled
        }
        categoryBreakdown {
          category
          repairs
          resales
          rentals
          refills
          recycled
        }
      }
      customerEngagement {
        monthlyActive
        retentionRate
        npsScore
        satisfactionScore
        monthlyTrends {
          month
          activeUsers
          retention
          satisfaction
        }
      }
    }
  }
`;

const valueFormatter = (number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number);

const percentFormatter = (number) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(number / 100);

export function CustomerAnalyticsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['customerAnalytics'],
    queryFn: () => fetchGraphQL(GET_CUSTOMER_ANALYTICS),
    placeholderData: MOCK_DATA // Use mock data while loading or if the query fails
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const {
    totalCustomers,
    newCustomersToday,
    activeCustomersThisWeek,
    averageLifetimeValue,
    loyaltyTierBreakdown,
    reCommerceActivity,
    customerEngagement
  } = data?.customerDashboard || MOCK_DATA.customerDashboard;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Text variant="h1" className="text-2xl font-bold">Customer Analytics</Text>
          <Text variant="body" className="text-muted-foreground">Analyze customer behavior and circular economy engagement</Text>
        </div>
        <Select defaultValue="30d">
          <SelectTrigger className="w-[180px]">
            <Calendar className="h-4 w-4 mr-2" />
            Time Range
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Customers"
          value={totalCustomers.toLocaleString()}
          icon={Users}
        />
        <MetricCard
          title="New Today"
          value={newCustomersToday.toLocaleString()}
          icon={Users}
        />
        <MetricCard
          title="Active This Week"
          value={activeCustomersThisWeek.toLocaleString()}
          icon={TrendingUp}
        />
        <MetricCard
          title="Average Lifetime Value"
          value={valueFormatter(averageLifetimeValue)}
          icon={ShoppingBag}
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="recommerce">Re-Commerce</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="sustainability">Sustainability</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Loyalty Tier Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <DashboardChart
                  type="pie"
                  title="Customer Segments"
                  data={loyaltyTierBreakdown.map(tier => ({
                    name: tier.tier,
                    value: tier.count,
                    percentage: tier.percentage
                  }))}
                  dataKey="value"
                  xAxisKey="name"
                  height={300}
                  colors={['#10B981', '#6366F1', '#F59E0B', '#3B82F6']}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Customer Engagement Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <DashboardChart
                  type="line"
                  title="Monthly Engagement"
                  data={customerEngagement.monthlyTrends}
                  dataKey="activeUsers"
                  xAxisKey="month"
                  height={300}
                  colors={['#10B981']}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="recommerce">
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricCard
                title="Repairs"
                value={reCommerceActivity.totalRepairs.toLocaleString()}
                icon={Wrench}
              />
              <MetricCard
                title="Resales"
                value={reCommerceActivity.totalResales.toLocaleString()}
                icon={RefreshCw}
              />
              <MetricCard
                title="Rentals"
                value={reCommerceActivity.totalRentals.toLocaleString()}
                icon={Repeat}
              />
              <MetricCard
                title="Refills"
                value={reCommerceActivity.totalRefills.toLocaleString()}
                icon={Droplet}
              />
              <MetricCard
                title="Recycled"
                value={reCommerceActivity.totalRecycled.toLocaleString()}
                icon={Recycle}
              />
              <MetricCard
                title="Re-Commerce Score"
                value={reCommerceActivity.averageReCommerceScore.toFixed(1)}
                icon={Star}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Re-Commerce Activity Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="line"
                    title="Monthly Trends"
                    data={reCommerceActivity.monthlyTrends}
                    dataKey="repairs,resales,rentals,refills,recycled"
                    xAxisKey="month"
                    height={300}
                    colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6', '#EC4899']}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Category Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="bar"
                    title="Re-Commerce by Category"
                    data={reCommerceActivity.categoryBreakdown}
                    dataKey="repairs,resales,rentals,refills,recycled"
                    xAxisKey="category"
                    height={300}
                    colors={['#6366F1', '#10B981', '#F59E0B', '#3B82F6', '#EC4899']}
                    stacked={true}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="engagement">
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Monthly Active"
                value={customerEngagement.monthlyActive.toLocaleString()}
                icon={Users}
              />
              <MetricCard
                title="Retention Rate"
                value={percentFormatter(customerEngagement.retentionRate)}
                icon={RefreshCw}
              />
              <MetricCard
                title="NPS Score"
                value={customerEngagement.npsScore.toFixed(1)}
                icon={TrendingUp}
              />
              <MetricCard
                title="Satisfaction"
                value={percentFormatter(customerEngagement.satisfactionScore)}
                icon={Star}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="line"
                    title="Monthly Trends"
                    data={customerEngagement.monthlyTrends}
                    dataKey="activeUsers,retention,satisfaction"
                    xAxisKey="month"
                    height={300}
                    colors={['#10B981', '#6366F1', '#F59E0B']}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Loyalty Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="bar"
                    title="Loyalty Tier Breakdown"
                    data={loyaltyTierBreakdown}
                    dataKey="count"
                    xAxisKey="tier"
                    height={300}
                    colors={['#6366F1']}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sustainability">
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reCommerceActivity.sustainabilityImpact.map((metric) => (
                <Card key={metric.metric}>
                  <CardHeader>
                    <CardTitle>{metric.metric}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-baseline gap-2">
                      <Text className="text-2xl font-bold">{metric.value}</Text>
                      <Badge variant={metric.change > 0 ? 'default' : 'secondary'}>
                        {metric.change > 0 ? '+' : ''}{metric.change}%
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Environmental Impact</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="bar"
                    title="Impact by Category"
                    data={reCommerceActivity.categoryBreakdown}
                    dataKey="recycled,refills"
                    xAxisKey="category"
                    height={300}
                    colors={['#10B981', '#3B82F6']}
                    stacked={true}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Circular Economy Adoption</CardTitle>
                </CardHeader>
                <CardContent>
                  <DashboardChart
                    type="line"
                    title="Monthly Adoption"
                    data={reCommerceActivity.monthlyTrends}
                    dataKey="recycled,refills"
                    xAxisKey="month"
                    height={300}
                    colors={['#10B981', '#3B82F6']}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
